/**
 * Recorder-facing panels shared by the producer console and the admin assist view:
 *   - RecordingToggle : opt this show in/out of transcript recording (pre-show or live)
 *   - ScriptHistory   : browse autosaved script versions and restore one
 *   - EndShowPanel    : mark the show completed → archive + download (producer)
 *
 * All are parametrised by callbacks so they work over either the producer's session
 * actions or the admin's direct socket emits.
 */
import { useEffect, useMemo, useState } from 'react';
import type { HistoryEntry } from '@goanna-im/shared';
import { getSocket } from '../lib/socket.js';
import { downloadBlob, getArchive, transcriptToText } from '../lib/archives.js';

export function RecordingToggle({
  recording,
  onToggle,
}: {
  recording: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <section className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Recording</h3>
        <span style={{ color: recording ? '#ef4444' : 'var(--muted)', fontWeight: 700, fontSize: '0.85rem' }}>
          {recording ? '● ON' : 'off'}
        </span>
      </div>
      <p className="muted" style={{ margin: 0, fontSize: '0.82rem' }}>
        When on, the presenter captures a timestamped transcript of the show. Choose this before you go
        live. The transcript is a download at the end; a copy stays with Super Admin for 30 days, then
        is deleted.
      </p>
      <button className={recording ? 'danger' : 'primary'} onClick={() => onToggle(!recording)}>
        {recording ? 'Stop recording' : 'Record this show'}
      </button>
    </section>
  );
}

export function ScriptHistory({
  listHistory,
  restoreHistory,
}: {
  listHistory: () => Promise<HistoryEntry[]>;
  restoreHistory: (at: number) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => setEntries(await listHistory());
  useEffect(() => {
    if (open) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <section className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Script history</h3>
        <button onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <>
          <p className="muted" style={{ margin: 0, fontSize: '0.82rem' }}>
            Autosaved versions of this show's script — restore one if an edit went wrong. Kept until the
            show is signed off, then archived.
          </p>
          <button onClick={() => void refresh()} disabled={busy}>
            Refresh
          </button>
          {entries.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>No versions captured yet.</p>
          ) : (
            <div className="stack" style={{ maxHeight: '30vh', overflow: 'auto' }}>
              {entries
                .slice()
                .reverse()
                .map((e) => (
                  <div key={e.at} className="row" style={{ justifyContent: 'space-between', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.85rem' }}>
                      {new Date(e.at).toLocaleTimeString('en-AU')} · v{e.version} · {e.title}
                      {e.by ? ` · ${e.by}` : ''}
                    </span>
                    <button
                      disabled={busy}
                      onClick={async () => {
                        if (!window.confirm('Restore this version? It replaces the current live script.')) return;
                        setBusy(true);
                        const ok = await restoreHistory(e.at);
                        setBusy(false);
                        if (ok) void refresh();
                        else window.alert('Could not restore that version.');
                      }}
                    >
                      Restore
                    </button>
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function EndShowPanel({ onSignoff }: { onSignoff: () => void }) {
  const socket = useMemo(() => getSocket(), []);
  const [archived, setArchived] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    const onArchived = (p: { id: string; title: string }) => setArchived(p);
    socket.on('session:archived', onArchived);
    return () => {
      socket.off('session:archived', onArchived);
    };
  }, [socket]);

  const download = async (kind: 'transcript' | 'json') => {
    if (!archived) return;
    const a = await getArchive(archived.id);
    if (!a) {
      window.alert('Could not fetch the archive.');
      return;
    }
    if (kind === 'transcript') {
      downloadBlob(`${a.title || 'show'}-transcript.txt`, transcriptToText(a), 'text/plain');
    } else {
      downloadBlob(`${a.title || 'show'}.json`, JSON.stringify(a, null, 2), 'application/json');
    }
  };

  return (
    <section className="card stack">
      <h3 style={{ marginTop: 0 }}>End show</h3>
      {!archived ? (
        <>
          <p className="muted" style={{ margin: 0, fontSize: '0.82rem' }}>
            Marks the show completed: saves a backup of the script (and version history) to long-term
            storage, and — if you recorded — makes the transcript available to download.
          </p>
          <button
            className="danger"
            onClick={() => {
              if (window.confirm('Mark this show completed? This signs off and archives it.')) onSignoff();
            }}
          >
            Mark show completed
          </button>
        </>
      ) : (
        <>
          <p style={{ margin: 0 }}>
            ✅ Archived: <strong>{archived.title}</strong>
          </p>
          <div className="row">
            <button className="primary" onClick={() => void download('transcript')}>
              Download transcript
            </button>
            <button onClick={() => void download('json')}>Download full archive</button>
          </div>
        </>
      )}
    </section>
  );
}
